import MentorshipStatusChip from 'components/Chips/MentorshipStatusChip';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import Radio from 'components/form/Inputs/Radio';
import Head from 'components/profileNavigation/Head';
import DeleteUser from 'components/UserInformation/DeleteUser';
import UserInformationDisplay from 'components/UserInformation/UserInformationDisplay';
import { MentorshipStatusValues } from 'enums/MentorshipStatus';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { mutate } from 'swr';
import User from 'types/User';
import { getTempUpdateAllowedMentorshipStatus } from 'utils/mentorshipTags';
import privateAPI from 'utils/privateAxiosInstance';
import { MenteeEditContext } from '.';

const ColumnTitle = styled.div`
  margin-bottom: 1rem;
  color: var(--gray-700);
  font-weight: 500;
  font-size: var() 0.9rem;
`;

type StatusDrawerProps = {
  open: boolean;
  close: () => void;
  mentee?: User;
};
const StatusDrawer: FC<StatusDrawerProps> = ({ open, close, mentee }) => {
  const [state, setState] = useState({
    mentorshipStatus: undefined as MentorshipStatusValues | undefined,
  });

  const resetState = useCallback(() => {
    if (mentee?.MenteeMentorship && mentee.MenteeMentorship[0])
      setState({
        mentorshipStatus: mentee.MenteeMentorship[0].status,
      });
  }, [mentee, setState]);

  useEffect(() => {
    resetState();
  }, [mentee, resetState]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tag: MentorshipStatusValues,
  ) => {
    setState({
      ...state,
      [e.target.name]: tag,
    });
  };

  const handleApply = () => {
    if (mentee?.menteeStatus === 'MENTORSHIP_ACTIVE') {
      return privateAPI
        .put(
          `/mentorship/temp/${
            mentee.MenteeMentorship && mentee.MenteeMentorship[0]?.id
          }`,
          {
            status: state.mentorshipStatus,
          },
        )
        .then(() => {
          mutate(`/user/${mentee.id}`);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const mentorshipStatusUpdateOptions: MentorshipStatusValues[] =
    useMemo(() => {
      if (!(mentee?.MenteeMentorship && mentee.MenteeMentorship[0]?.status))
        return [];
      return getTempUpdateAllowedMentorshipStatus(
        mentee.MenteeMentorship[0].status,
      );
    }, [mentee]);

  return (
    <ActionDrawer
      open={open}
      close={close}
      title="Modifier le statut"
      subtitle={`Modifier le statut de ${mentee?.firstName} ${mentee?.lastName}`}
      cancel={resetState}
      apply={handleApply}
    >
      {mentee?.menteeStatus === 'MENTORSHIP_ACTIVE' &&
      mentorshipStatusUpdateOptions[0] ? (
        <>
          <ColumnTitle>Statut du mentorat</ColumnTitle>
          <CheckboxList>
            {mentorshipStatusUpdateOptions.map((tag, idx) => {
              return (
                <Radio
                  key={idx}
                  label={<MentorshipStatusChip status={tag} />}
                  checked={state.mentorshipStatus === tag}
                  onChange={(e) => handleChange(e, tag)}
                  name="mentorshipStatus"
                  id={tag}
                />
              );
            })}
          </CheckboxList>
        </>
      ) : (
        "Aucun changement de statut n'est actuellement possible pour cet utilisateur."
      )}
    </ActionDrawer>
  );
};

const GeneralInformation: FC = () => {
  const { mentee } = useContext(MenteeEditContext);
  const [statusOpen, setStatusOpen] = useState(false);
  return (
    <>
      <StatusDrawer
        open={statusOpen}
        close={() => setStatusOpen(false)}
        mentee={mentee}
      />
      <section>
        <Head
          title="Informations générales"
          subtitle="Liste toutes les informations sur l'utilisateur"
        />
        <div>
          <UserInformationDisplay
            user={mentee}
            variant="mentee"
            reader="admin"
            setStatusOpen={setStatusOpen}
          />
        </div>
        <DeleteUser id={mentee?.id || -1} redirect="/admin/mentoring/mentees" />
      </section>
    </>
  );
};

export default GeneralInformation;
